import { useCallback, useEffect, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../lib/firebase';

export interface ChannelAttribution {
  source: string;
  provider: string | null;
  campaignId: string | null;
  campaignName: string | null;
  spendCents: number;
  impressions: number;
  clicks: number;
  leads: number;
  contacted: number;
  converted: number;
  // Null means WITHHELD for this role, never zero. A marketing operator shown
  // "$0" would reasonably conclude the channel earned nothing.
  settledRevenueCents: number | null;
  cplCents: number | null;
  cpcCents: number | null;
  // Basis points (10000 = 1x return). Format as `bps / 100` for percent.
  roasBps: number | null;
}

export interface LeadAttributionTotals {
  spendCents: number;
  impressions: number;
  clicks: number;
  leads: number;
  contacted: number;
  converted: number;
  settledRevenueCents: number | null;
  cplCents: number | null;
  roasBps: number | null;
}

export interface SourceDistributionRow {
  source: string;
  leads: number;
  percentage: number;
  colorHex: string;
}

export interface ConversionRateRow {
  source: string;
  conversionPct: number;
  // Sample size, so the page can refuse to crown a "best" source off one lead.
  leads: number;
  converted: number;
  colorHex: string;
}

export interface LeadAttributionData {
  windowStart: string;
  windowEnd: string;
  /** Business timezone the window is interpreted in (Australia/Brisbane). */
  timezone?: string;
  totals: LeadAttributionTotals;
  channels: ChannelAttribution[];
  /** True when commission was withheld for this role rather than being zero. */
  revenueRedacted?: boolean;
  /** Plain-English basis for the commission figure, rendered on the tile. */
  commissionBasisNote?: string;
  sourceDistribution: SourceDistributionRow[];
  conversionRates: ConversionRateRow[];
}

interface Params {
  companyId: string | undefined;
  windowStart?: string;
  windowEnd?: string;
}

interface State {
  data: LeadAttributionData | null;
  loading: boolean;
  error: string | null;
  refresh: () => void;
}

export function useLeadAttribution(params: Params): State {
  const { companyId, windowStart, windowEnd } = params;
  const [data, setData] = useState<LeadAttributionData | null>(null);
  const [loading, setLoading] = useState<boolean>(Boolean(companyId));
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState<number>(0);

  const refresh = useCallback(() => setRefreshKey((k) => k + 1), []);

  useEffect(() => {
    if (!companyId) {
      setLoading(false);
      return;
    }
    const fn = httpsCallable<
      { companyId: string; windowStart?: string; windowEnd?: string },
      LeadAttributionData
    >(functions, 'getLeadAttribution');
    setLoading(true);
    setError(null);
    fn({ companyId, windowStart, windowEnd })
      .then((r) => setData(r.data))
      .catch((err) => {
        console.error('[useLeadAttribution] fetch failed', err);
        setError(err instanceof Error ? err.message : String(err));
      })
      .finally(() => setLoading(false));
  }, [companyId, windowStart, windowEnd, refreshKey]);

  return { data, loading, error, refresh };
}
